import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const DataRefreshContext = createContext(null);

const CORE_RESOURCES = ["bookings", "invoices", "enquiries", "properties", "quotes", "options"];

// Module-level ref for non-React code (axios interceptor) to trigger refreshes
let _triggerRefreshRef = null;
export function getDataRefreshTrigger() {
  return _triggerRefreshRef;
}

export function DataRefreshProvider({ children }) {
  const [versions, setVersions] = useState({});
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);

  const triggerRefresh = useCallback((resourceTypes) => {
    const types = Array.isArray(resourceTypes) ? resourceTypes : [resourceTypes];
    types.forEach((r) => pendingRef.current.add(r));

    // Debounce: batch all triggers within 300ms
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const toFlush = Array.from(pendingRef.current);
      pendingRef.current.clear();
      setVersions((prev) => {
        const next = { ...prev };
        toFlush.forEach((r) => {
          next[r] = (next[r] || 0) + 1;
        });
        // Auto-bump dashboard when core resources change
        if (toFlush.some((r) => CORE_RESOURCES.includes(r))) {
          next.dashboard = (next.dashboard || 0) + 1;
        }
        return next;
      });
    }, 300);
  }, []);

  // Expose triggerRefresh to non-React code
  useEffect(() => {
    _triggerRefreshRef = triggerRefresh;
    return () => { _triggerRefreshRef = null; };
  }, [triggerRefresh]);

  // SSE connection for server-pushed resource change events
  useEffect(() => {
    let eventSource;
    let reconnectTimer;
    let backoff = 2000; // start at 2s, double on each failure, cap at 60s
    let retryCount = 0;
    const MAX_RETRIES = 50;

    const connect = () => {
      try {
        eventSource = new EventSource("/api/events/stream");

        eventSource.onopen = () => {
          backoff = 2000; // reset on successful connection
          retryCount = 0;
        };

        eventSource.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data);
            if (parsed.type === "resource_changed" && parsed.data?.resource) {
              triggerRefresh(parsed.data.resource);
            }
          } catch {
            // Ignore parse errors (keepalive comments)
          }
        };

        eventSource.onerror = () => {
          // Close immediately to prevent the browser's built-in rapid reconnect
          eventSource.close();
          retryCount++;
          if (retryCount < MAX_RETRIES) {
            reconnectTimer = setTimeout(connect, backoff);
            backoff = Math.min(backoff * 2, 60000);
          }
        };
      } catch {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          reconnectTimer = setTimeout(connect, backoff);
          backoff = Math.min(backoff * 2, 60000);
        }
      }
    };

    connect();

    return () => {
      if (eventSource) eventSource.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [triggerRefresh]);

  return (
    <DataRefreshContext.Provider value={{ versions, triggerRefresh }}>
      {children}
    </DataRefreshContext.Provider>
  );
}

/**
 * Subscribe to refresh triggers for one or more resource types.
 * Returns a version counter suitable as a useEffect dependency.
 *
 * Usage:
 *   const refreshKey = useRefreshTrigger("bookings");
 *   useEffect(() => { fetchData(); }, [refreshKey]);
 */
export function useRefreshTrigger(resourceTypes) {
  const ctx = useContext(DataRefreshContext);
  if (!ctx) return 0;
  const types = Array.isArray(resourceTypes) ? resourceTypes : [resourceTypes];
  return types.reduce((sum, r) => sum + (ctx.versions[r] || 0), 0);
}

/**
 * Returns the raw versions object from the DataRefreshContext.
 * Used by useSSEInvalidation to bridge SSE events into TanStack Query.
 */
export function useRefreshVersions() {
  const ctx = useContext(DataRefreshContext);
  if (!ctx) return {};
  return ctx.versions;
}

/**
 * Get the triggerRefresh function for use after local mutations.
 *
 * Usage:
 *   const triggerRefresh = useDataRefresh();
 *   await updateBooking(id, data);
 *   triggerRefresh("bookings");
 */
export function useDataRefresh() {
  const ctx = useContext(DataRefreshContext);
  if (!ctx) return () => {};
  return ctx.triggerRefresh;
}
